import { buttonVariants } from '@src/design-system/button';
import { DialogContent, DialogTrigger } from '@src/design-system/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { cn } from '@src/design-system/helpers';
import type { TLeadNote } from '@src/shared/types/lead-note';
import { EllipsisVerticalIcon, Trash } from 'lucide-react';
import { useState } from 'react';
import { Dialog } from '@src/design-system/dialog';
import { CloseCircle, Eye, TickCircle } from 'iconsax-react';
import leadNoteApi from '../../services/lead-note-api';
import { LeadNotesAddDialog } from '../../pages/list/dialogs/lead-notes-add-dialog';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';

export const NoteCardHeaderActions = ({ note }: { note: TLeadNote }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          buttonVariants({ size: 'iconXs', variant: 'primaryOutline' }),
          'border-primary-200 size-6 rounded-md'
        )}
      >
        <EllipsisVerticalIcon />
      </DropdownMenuTrigger>

      <DropdownMenuContent align='end'>
        <NoteCardViewAction note={note} />
        {note.next_follow_up_date ? (
          <NoteCardResolveAction note={note} />
        ) : null}
        <NoteCardDeleteAction note={note} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NoteCardDeleteAction = ({ note }: { note: TLeadNote }) => {
  const { mutateAsync: deleteNote, isPending } = leadNoteApi.useDelete();

  const handleDelete = async (closeModal: () => void) => {
    await deleteNote({ id: note.id });
    closeModal();
  };

  return (
    <Dialog>
      <DropdownMenuItem
        onClick={(e) => {
          e.preventDefault();
        }}
        className='text-red-500'
      >
        <DialogTrigger asChild>
          <div className='flex items-center gap-2'>
            <Trash size={16} /> Delete
          </div>
        </DialogTrigger>
      </DropdownMenuItem>

      <DialogContent align='center'>
        <DeleteConfirmDialog
          title='Are you sure you want to delete this note?'
          isLoading={isPending}
          onConfirm={handleDelete}
        />
      </DialogContent>
    </Dialog>
  );
};

const NoteCardViewAction = ({ note }: { note: TLeadNote }) => {
  const [openNewNote, setOpenNewNote] = useState(false);
  return (
    <>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpenNewNote(true);
        }}
      >
        <Eye size={16} /> View
      </DropdownMenuItem>

      <Dialog
        open={openNewNote}
        onOpenChange={setOpenNewNote}
      >
        <DialogContent>
          <LeadNotesAddDialog
            note={note}
            leadId={note.lead?.id}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const NoteCardResolveAction = ({ note }: { note: TLeadNote }) => {
  const isResolved = note.is_resolved;
  const { mutateAsync: resolveNote } = leadNoteApi.useUpdate();
  const handleResolve = async () => {
    await resolveNote({ id: note.id, is_resolved: !isResolved });
  };
  return (
    <DropdownMenuItem onClick={handleResolve}>
      {isResolved ? (
        <div className='flex items-center gap-2'>
          <CloseCircle size={16} />
          <span className='text-sm'>Unresolved</span>
        </div>
      ) : (
        <div className='flex items-center gap-2'>
          <TickCircle size={16} />
          <span className='text-sm'>Resolved</span>
        </div>
      )}
    </DropdownMenuItem>
  );
};
